import { amazonConnectApi, type OrderRow } from '../../api/amazonconnect';
import { fetchInventorySample, fetchOrdersScan } from '../daily-brief/loadDailyBrief';
import { yesterdayWindows } from '../daily-brief/dayWindows';
import type { DailyBriefModel } from '../daily-brief/types';
import type { ExecutiveKpiStripModel } from '../executive-kpis/types';
import type { IndiaMapModel } from '../india-map/types';
import type { MasterOverlayModel } from '../master-overlay/types';
import type { OpsIntelligenceModel } from '../ops-intelligence/types';
import type { SalesPulseModel } from '../sales-pulse-24h/types';
import {
  dailyBriefCalcCsv,
  executiveKpiCalcCsv,
  indiaMapCalcCsv,
  opsCalcCsv,
  overlayCalcCsv,
  pulseCalcCsv,
} from './blockCsv';
import { downloadCsv, toCsv } from './csv';

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function stamp(): string {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

function ordersScanCsv(orders: OrderRow[]): string {
  return toCsv(
    [
      'amazon_order_id',
      'purchase_date',
      'purchase_date_ist',
      'order_status',
      'quantity',
      'order_total',
      'currency',
      'asin',
      'sku',
      'fulfilment',
      'marketplace_id',
      'source_raw_id',
    ],
    orders.map((o) => [
      o.amazon_order_id,
      o.purchase_date,
      o.purchase_date_ist,
      o.order_status,
      o.quantity,
      o.order_total,
      o.currency,
      o.asin,
      o.sku,
      o.fulfilment,
      o.marketplace_id,
      o.source_raw_id,
    ]),
  );
}

export type DebugExportModels = {
  connectionId: string | null;
  brief: DailyBriefModel | null;
  kpis: ExecutiveKpiStripModel | null;
  overlay: MasterOverlayModel | null;
  pulse: SalesPulseModel | null;
  ops: OpsIntelligenceModel | null;
  map: IndiaMapModel | null;
};

/** Downloads per-block calc CSVs plus API raw dumps (browser Downloads folder). */
export async function downloadAllDebugCsvs(models: DebugExportModels): Promise<void> {
  const t = stamp();
  const files: Array<{ name: string; csv: string }> = [];

  if (models.brief) {
    files.push({ name: `dashboard_${t}_daily-brief.csv`, csv: dailyBriefCalcCsv(models.brief) });
  }
  if (models.kpis) {
    files.push({ name: `dashboard_${t}_executive-kpis.csv`, csv: executiveKpiCalcCsv(models.kpis) });
  }
  if (models.overlay) {
    files.push({ name: `dashboard_${t}_overlay.csv`, csv: overlayCalcCsv(models.overlay) });
  }
  if (models.pulse) {
    files.push({ name: `dashboard_${t}_pulse.csv`, csv: pulseCalcCsv(models.pulse) });
  }
  if (models.ops) {
    files.push({ name: `dashboard_${t}_ops.csv`, csv: opsCalcCsv(models.ops) });
  }
  if (models.map) {
    files.push({ name: `dashboard_${t}_india-map.csv`, csv: indiaMapCalcCsv(models.map) });
  }

  const index = toCsv(
    ['file', 'block', 'save_hint'],
    files.map((f) => [f.name, f.name.replace(/^dashboard_[^_]+_/, '').replace(/\.csv$/, ''), 'exports/debug/dashboard']),
  );
  files.unshift({ name: `dashboard_${t}_index.csv`, csv: index });

  const conn = models.connectionId;
  if (conn) {
    const from = models.kpis?.from ?? models.map?.periodFrom ?? yesterdayWindows().yesterday.isoFrom;
    const to = models.kpis?.to ?? models.map?.periodTo ?? yesterdayWindows().yesterday.isoTo;
    const y = yesterdayWindows().yesterday;

    try {
      const scan = await fetchOrdersScan(conn);
      files.push({
        name: `raw_${t}_orders_scan.csv`,
        csv: ordersScanCsv(scan.orders),
      });
      files.push({
        name: `raw_${t}_orders_scan_meta.csv`,
        csv: toCsv(
          ['connection_id', 'scanned', 'pages', 'store_total', 'truncated', 'source'],
          [[conn, scan.orders.length, scan.pages, scan.total, scan.truncated, scan.source]],
        ),
      });
    } catch (e) {
      files.push({
        name: `raw_${t}_orders_scan_error.csv`,
        csv: toCsv(['error'], [[e instanceof Error ? e.message : String(e)]]),
      });
    }

    try {
      const [periodMet, yMet, geo, inv, pricing] = await Promise.all([
        amazonConnectApi.getOrdersMetrics(conn, { from, to, tz: 'Asia/Kolkata' }),
        amazonConnectApi.getOrdersMetrics(conn, {
          from: y.isoFrom,
          to: y.isoTo,
          tz: 'Asia/Kolkata',
        }),
        amazonConnectApi.getAllOrdersGeo(conn, { from, to, tz: 'Asia/Kolkata' }).catch(() => null),
        fetchInventorySample(conn).catch(() => null),
        amazonConnectApi
          .listPricingPage(conn, { page: 1, page_size: 100 })
          .catch(() => null),
      ]);

      files.push({
        name: `raw_${t}_orders_metrics.csv`,
        csv: toCsv(
          [
            'window',
            'from',
            'to',
            'timezone',
            'sales',
            'order_items',
            'units',
            'orders',
            'source',
            'complete',
            'incomplete_reason',
            'currency',
          ],
          [
            [
              'kpi_period',
              periodMet.from,
              periodMet.to,
              periodMet.timezone,
              periodMet.sales,
              periodMet.order_items,
              periodMet.units,
              periodMet.orders ?? '',
              periodMet.source ?? '',
              periodMet.complete,
              periodMet.incomplete_reason ?? '',
              periodMet.currency ?? '',
            ],
            [
              'yesterday',
              yMet.from,
              yMet.to,
              yMet.timezone,
              yMet.sales,
              yMet.order_items,
              yMet.units,
              yMet.orders ?? '',
              yMet.source ?? '',
              yMet.complete,
              yMet.incomplete_reason ?? '',
              yMet.currency ?? '',
            ],
          ],
        ),
      });

      if (geo) {
        files.push({
          name: `raw_${t}_all_orders_geo_cities.csv`,
          csv: toCsv(
            ['city', 'state', 'orders', 'units', 'sales', 'source', 'line_count'],
            geo.cities.map((c) => [
              c.city,
              c.state,
              c.orders,
              c.units,
              c.sales,
              geo.source,
              geo.line_count,
            ]),
          ),
        });
        files.push({
          name: `raw_${t}_all_orders_geo_states.csv`,
          csv: toCsv(
            ['state', 'orders', 'units', 'sales', 'source', 'line_count'],
            geo.states.map((s) => [
              s.state,
              s.orders,
              s.units,
              s.sales,
              geo.source,
              geo.line_count,
            ]),
          ),
        });
      }

      if (inv) {
        files.push({
          name: `raw_${t}_inventory_sample.csv`,
          csv: toCsv(
            ['sku', 'asin', 'available', 'reserved', 'inbound', 'fulfilled', 'fba_inventory'],
            inv.rows.map((r) => [
              r.sku,
              r.asin,
              r.available,
              r.reserved,
              r.inbound,
              r.fulfilled,
              r.fba_inventory,
            ]),
          ),
        });
      }

      if (pricing) {
        const items = pricing.items ?? [];
        files.push({
          name: `raw_${t}_pricing_sample.csv`,
          csv: toCsv(
            ['sku', 'asin', 'buy_box_winner', 'buy_box_pct', 'landed_price'],
            items.map((p) => [
              p.sku,
              p.asin,
              p.buy_box_winner,
              p.buy_box_pct,
              p.landed_price,
            ]),
          ),
        });
      }
    } catch (e) {
      files.push({
        name: `raw_${t}_api_error.csv`,
        csv: toCsv(['error'], [[e instanceof Error ? e.message : String(e)]]),
      });
    }
  }

  for (const f of files) {
    downloadCsv(f.name, f.csv);
    await sleep(250);
  }
}
